import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { NumberOrderApi, NumberOrderOrganizationInfoApi } from "../../../../../services/Api/Public/types";
import isElementClippingAnotherElement from "../../../../../utils/elements/isElementClippingAnotherElement";
import { getNumberSystemClassName, getNumberSystemFontSize } from "../utils";

interface Props {
	item: NumberOrderApi;
	parentRef: RefObject<HTMLDivElement>;
	organizationInfo: NumberOrderOrganizationInfoApi | undefined;
}

const getClippingClass = (itemRef: React.RefObject<HTMLDivElement>, parentRef: RefObject<HTMLDivElement>) => {
	if (!itemRef.current || !parentRef.current) return "opacity-0";

	return isElementClippingAnotherElement(itemRef.current, parentRef.current) ? "opacity-0" : "";
};

const NumberSystemCard = ({ item, parentRef, organizationInfo }: Props) => {
	const itemRef = useRef<HTMLDivElement>(null);
	const [clippingClass, setClippingClass] = useState(getClippingClass(itemRef, parentRef));
	const fontSize = getNumberSystemFontSize(organizationInfo);

	useEffect(() => {
		setClippingClass(getClippingClass(itemRef, parentRef));
	}, [itemRef, parentRef]);

	// Calculate dynamic card dimensions based on font size
	const cardStyle = useMemo(() => {
		// Default font size is 64px
		// Base values: width: 163px, height: 135px for font size 64px

		// Calculate width and height dynamically based on font size
		// For font size 64px: width = 64 * 2.55 = 163.2px, height = 64 * 2.1 = 134.4px
		// This maintains the same ratio for all font sizes
		const widthRatio = 2.55; // 163/64 ≈ 2.55
		const heightRatio = 2.1; // 135/64 ≈ 2.1

		// Add a small padding to ensure text fits within the card
		const padding = fontSize * 0.2;

		const cardWidth = fontSize * widthRatio + padding;
		const cardHeight = fontSize * heightRatio + padding;

		// Calculate border-radius dynamically based on font size
		// For font size 64px, border-radius should be 24px
		// For smaller font sizes, scale proportionally to maintain rounded rectangle look
		const borderRadiusRatio = 0.375; // 24/64 = 0.375
		const borderRadius = Math.max(8, Math.round(fontSize * borderRadiusRatio));

		return {
			width: `${cardWidth}px`,
			height: `${cardHeight}px`,
			borderRadius: `${borderRadius}px`,
		};
	}, [fontSize]);

	// Calculate border width based on font size
	const borderWidth = Math.max(1, Math.floor(fontSize / 20));
	const cardClassName = getNumberSystemClassName(item);

	return (
		<Card
			className={`number-system-card ${cardClassName} ${clippingClass}`}
			ref={itemRef}
			style={{
				...cardStyle,
				...(cardClassName === "default" ? { borderWidth: `${borderWidth}px` } : {}),
			}}
		>
			<Card.Body>
				<p style={{ fontSize: `${fontSize}px` }}>{item.order_number}</p>
			</Card.Body>
		</Card>
	);
};

export default NumberSystemCard;
